import { useConnectModal } from '@rainbow-me/rainbowkit';
import React, {useEffect, useState} from 'react';
import { observer } from 'mobx-react';
import { useAccount } from 'wagmi';
import { useBreakpointContext } from '../../context-providers/BreakpointProvider';
import { formatCurrencyAmount } from '../../helpers/formatters';
import { socketService } from '../../services/socket/socket.service';
import { marketsStore } from '../../stores/MarketsStore';
import BalanceWidget from '../BalanceWidget/BalanceWidget';
import { TVChartContainer } from '../Chart/TVChartContainer';
import NereusConnectButton from '../NereusConnectButton/NereusConnectButton';
import OpenPosition from '../OpenPosition/OpenPosition';
import Toolbar from '../Toolbar/Toolbar';
import './MarketPage.scss';
import { useMarketRoute } from '../../hooks/useMarketRoute';
import TradeTabsNavigation from '../trade-tabs/TradeTabs/TradeTabsNavigation';
import { positionsStore } from '../../services/position/position.store';
import {Outlet, useNavigate} from "react-router-dom";
import NereusButton from "../NereusButton/NereusButton";

const defaultTitle = 'Nereus';

const MarketPage = observer(() => {

    const { isConnected } = useAccount();
    const { openConnectModal } = useConnectModal();
    const { md, sm, xs } = useBreakpointContext();
    const currentMarket = marketsStore.currentMarket;
    const selectingMarket = marketsStore.selectingMarket;
    const [isBridgeModalOpen, openBridgeModal] = useState(false)
    const navigate = useNavigate();

    const handleOpenBridgeModal = () => {
        if (currentMarket) {
            navigate(`/markets/${currentMarket.marketId}/bridge`);
        }
        openBridgeModal(true)
    };
    const handleCloseBridgeModal = () => {
        if (currentMarket) {
            navigate(`/markets/${currentMarket.marketId}`);
        }
        openBridgeModal(false)
    };

    useEffect(() => {

        if (currentMarket) {
            document.title = `${marketsStore.latestPrice > 0 ? formatCurrencyAmount(marketsStore.latestPrice, '$'): ''} · ${currentMarket?.marketId} · Nereus`;
        } else {
            document.title = defaultTitle;
        }

        return () => {
            document.title = defaultTitle;
        };
    }, [currentMarket?.marketId, defaultTitle, marketsStore.latestPrice]);

    useEffect(() => {
        marketsStore.fetchMarkets()
    }, []);

    useMarketRoute();

    useEffect(() => {
        if (!socketService.isConnected || !currentMarket?.ticker) {
            return;
        }

        if (selectingMarket) {
            socketService.subscribeToTickers(marketsStore.allMarketTickers);
        } else {
            const tickers = new Set([currentMarket.ticker, ...positionsStore.openPositionsTickers])
            socketService.subscribeToTickers(Array.from(tickers));
        }

    }, [selectingMarket, currentMarket?.ticker, socketService.isConnected, positionsStore.openPositionsTickers]);

    const bridgeButton = (
        <NereusButton className="flex-1" variant="outlined" color="secondary" onClick={handleOpenBridgeModal}>
            Bridge
        </NereusButton>
    )

    return (
        <div className="Market-content content-container">
            <main className="Market-content__main">
                <Toolbar/>
                <div className="card-container">
                    {currentMarket &&
                        <TVChartContainer baseAsset={currentMarket.baseAsset} quoteAsset={currentMarket.quoteAsset}/>}
                </div>

                <div className="mt-5">
                    {md && <TradeTabsNavigation/>}
                </div>
            </main>
            <aside className="Market-content__sidebar">
                <div className="App__sidebar__deposit card-container p-4">
                    {/* BalanceWidget is inside NereusConnectButton to handle case with wrong network */}
                    {isConnected
                        ? (
                            <>
                                {isBridgeModalOpen ? <Outlet context={{onClose: handleCloseBridgeModal}}/> : null}
                                <NereusConnectButton bridgeButton={bridgeButton}>
                                    <BalanceWidget bridgeButton={bridgeButton}/>
                                </NereusConnectButton>
                            </>
                        )
                        : (
                            <div className="Market-content__sidebar__connectWrapper">
                                <div className="nereus-caption">
                                    <p className="nereus-color-neutral-grey">No wallet connect</p>
                                    <p className="nereus-color-main cursor-pointer" onClick={openConnectModal}>Connect wallet to deposit</p>
                                </div>
                                <NereusConnectButton/>
                            </div>
                        )}

                </div>
                <div>
                    {currentMarket && (<OpenPosition marketInfo={currentMarket}/>)}
                </div>
                <div className="mt-5">
                    {(sm || xs) && <TradeTabsNavigation/>}
                </div>
            </aside>
        </div>
    );
});

export default MarketPage;